import { useEffect, useState } from 'react';
import { api } from '../api.js';
import { friendlyDeviceTypeLabel } from '../devices.js';
import { rememberAfterOnboarding } from '../after-onboarding.js';

// Landing page for the QR-scan claim flow: device renders a QR pointing
// at https://aquasensei.app/app/claim?cid=<account>/<display>, the user
// scans with their phone's native Camera (universal-link semantics open
// the SPA here), and we walk them through linking it to a tank. Falls
// back gracefully when `cid` is missing or the device isn't seen yet.

const POLL_MS = 5_000;
// A display first reaches the server with its first Apex readings, which can
// be several minutes after it joins Wi-Fi (the Trident searches the network for
// the Apex), so the wait covers that rather than giving up after two minutes.
const POLL_DURATION_MS = 10 * 60 * 1000;

export function ClaimDevicePage({ user }) {
  const params = new URLSearchParams(window.location.search);
  const cidParam = (params.get('cid') || '').trim();

  const [cid, setCid] = useState(cidParam);
  const [lookup, setLookup] = useState(null); // null = loading, object = result
  const [tanks, setTanks] = useState(null);
  const [pick, setPick] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(null);
  // Bumped by "Check again" to restart the two-minute wait.
  const [pollRun, setPollRun] = useState(0);
  const [polling, setPolling] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let pollStop = null;
    if (!cid) {
      setLookup({ ready: true, found: false, missingCid: true });
      return;
    }
    setPolling(true);
    const tick = async () => {
      try {
        const r = await api(`/api/devices/by-cid/${encodeURIComponent(cid)}`);
        if (cancelled) return;
        setLookup({ ready: true, ...r });
        if (r.found) {
          if (pollStop) clearInterval(pollStop);
        }
      } catch (e) {
        if (!cancelled) {
          setLookup({ ready: true, found: false, error: e.message });
        }
      }
    };
    tick();
    // Poll every 5s for up to 2 minutes for the device-not-seen case.
    pollStop = setInterval(tick, POLL_MS);
    const stopAt = setTimeout(() => {
      if (pollStop) clearInterval(pollStop);
      if (!cancelled) setPolling(false);
    }, POLL_DURATION_MS);
    return () => {
      cancelled = true;
      if (pollStop) clearInterval(pollStop);
      clearTimeout(stopAt);
    };
  }, [cid, pollRun]);

  useEffect(() => {
    if (!user || tanks !== null) return;
    api('/api/tanks')
      // A display can only be linked to the keeper's own tanks; a tank
      // shared with them is refused by the claim.
      .then((rows) => setTanks((rows || []).filter((t) => !t.role || t.role === 'owner')))
      // Not "you have no tank": on weak fish-room Wi-Fi the list just failed.
      .catch(() => setTanks(false));
  }, [user, tanks]);

  const submit = () => {
    if (!pick) {
      setErr('Pick a tank for this device.');
      return;
    }
    linkTo(pick);
  };

  const linkTo = async (tankId) => {
    setBusy(true);
    setErr('');
    try {
      const r = await api('/api/devices/claim', {
        method: 'POST',
        body: { cid, tank_id: tankId },
      });
      setDone(r);
    } catch (e) {
      const code = e.payload?.error;
      setErr(messageFor(code) || e.message || 'Could not link that device.');
    } finally {
      setBusy(false);
    }
  };

  if (!user) {
    // Bounce through the sign-in screen; we'll be back at this URL. That screen
    // is email first and makes an account for a new address, so this must not
    // read as "only for people who already have an account".
    const next = encodeURIComponent(window.location.pathname + window.location.search);
    return (
      <main className="claim-page" style={shellStyle}>
        <h1 className="font-heading">Add your display to TankSensei</h1>
        <p>
          TankSensei keeps your tank&rsquo;s readings, alerts and history in one place, and it is
          free during beta. Use your email to sign in, or to make an account if you don&rsquo;t have
          one yet. We bring you back here to finish.
        </p>
        <a
          href={`/app/?next=${next}`}
          className="ts-save"
          style={{ display: 'inline-block', marginTop: 12, minHeight: 44 }}
        >
          Continue with email
        </a>
      </main>
    );
  }

  if (!lookup?.ready) {
    return (
      <main className="claim-page" style={shellStyle}>
        <p>Looking up that device…</p>
      </main>
    );
  }

  if (done) {
    return (
      <main className="claim-page" style={shellStyle}>
        <h1 className="font-heading">Linked.</h1>
        <p>
          Your {friendlyDeviceTypeLabel(done.device?.device_type, 'device')} is linked to{' '}
          <strong>{done.tank?.name}</strong>. New readings will land automatically.
        </p>
        <a href="/app/" className="ts-save" style={{ display: 'inline-block', marginTop: 12 }}>
          Open TankSensei
        </a>
      </main>
    );
  }

  if (lookup.missingCid || !cid) {
    return (
      <main className="claim-page" style={shellStyle}>
        <h1 className="font-heading">Link a device</h1>
        <p>
          On the display, open its pairing screen: on the Trident OLED, swipe down and tap SETUP,
          then LINK; on the 7-inch display, Settings, then Pair with phone. Scan the code with your
          phone&rsquo;s camera, or type the ID shown under it here.
        </p>
        <ManualEntry value={cid} onChange={setCid} />
        <p style={{ marginTop: 16 }}>
          <a href="/app/">Back to TankSensei</a>
        </p>
      </main>
    );
  }

  if (!lookup.found) {
    return (
      <main className="claim-page" style={shellStyle}>
        <h1 className="font-heading">Waiting for your display…</h1>
        <p>
          We haven&rsquo;t heard from <code>{cid}</code> yet. A display first reaches TankSensei
          once it is on Wi-Fi and showing your Apex&rsquo;s numbers.{' '}
          {polling
            ? 'Keep this page open; we keep checking for ten minutes.'
            : 'We stopped checking. Tap Check again once the display shows your numbers.'}
        </p>
        <p>
          If the display says NO WI-FI, SETUP, NO APEX or APEX LOGIN, that comes first:{' '}
          <a href="/app/device-help">see what to do</a>, or on the display swipe down, tap SETUP,
          then HELP.
        </p>
        {Array.isArray(tanks) && tanks.length === 0 && <NoTankYet user={user} />}
        {lookup.error && <p className="ts-hint">{lookup.error}</p>}
        {!polling && (
          <button
            type="button"
            className="ts-save"
            style={{ marginTop: 8 }}
            onClick={() => setPollRun((n) => n + 1)}
          >
            Check again
          </button>
        )}
        <ManualEntry value={cid} onChange={setCid} />
        <p style={{ marginTop: 16 }}>
          <a href="/app/">Back to TankSensei</a>
        </p>
      </main>
    );
  }

  if (lookup.claimed && !lookup.claimed_by_you) {
    return (
      <main className="claim-page" style={shellStyle}>
        <h1 className="font-heading">Already in use</h1>
        <p>
          That device is already linked to another TankSensei account. If it should be yours,{' '}
          <a href="/app/device-help#write-to-us">write to us</a> and we&rsquo;ll sort it out.
        </p>
      </main>
    );
  }

  if (tanks === null) {
    return (
      <main className="claim-page" style={shellStyle}>
        <p>Looking up your tanks…</p>
        <p style={{ marginTop: 16 }}>
          <a href="/app/">Back to TankSensei</a>
        </p>
      </main>
    );
  }

  if (tanks === false) {
    return (
      <main className="claim-page" style={shellStyle}>
        <h1 className="font-heading">Link this device</h1>
        <p>Couldn&rsquo;t load your tanks. Check your connection and try again.</p>
        <button
          type="button"
          className="ts-save"
          style={{ marginTop: 12 }}
          onClick={() => setTanks(null)}
        >
          Try again
        </button>
        <p style={{ marginTop: 16 }}>
          <a href="/app/">Back to TankSensei</a>
        </p>
      </main>
    );
  }

  const myTanks = tanks;
  const { mode, here: alreadyHere } = claimMode(lookup, myTanks);

  if (mode === 'linked') {
    return (
      <main className="claim-page" style={shellStyle}>
        <h1 className="font-heading">Already linked</h1>
        <p>
          Your {friendlyDeviceTypeLabel(lookup.device?.device_type, 'device')} is linked to{' '}
          <strong>{alreadyHere.name}</strong>. If its screen still shows the pairing code, send the
          link to it again.
        </p>
        {err && (
          <div role="alert" className="ts-err" style={{ marginTop: 8 }}>
            {err}
          </div>
        )}
        <div className="ts-row-body" style={{ marginTop: 12 }}>
          <button
            type="button"
            className="ts-save"
            onClick={() => linkTo(alreadyHere.id)}
            disabled={busy}
          >
            {busy ? '…' : 'Send to the display'}
          </button>
          <a href="/app/" className="ts-chip">
            Open TankSensei
          </a>
        </div>
      </main>
    );
  }

  return (
    <main className="claim-page" style={shellStyle}>
      <h1 className="font-heading">{alreadyHere ? 'Move this device' : 'Link this device'}</h1>
      <p>
        {friendlyDeviceTypeLabel(lookup.device?.device_type)} <code>{cid}</code>
        {alreadyHere ? ` is linked to ${alreadyHere.name}. Move it to:` : ' — pick a tank:'}
      </p>

      {myTanks.length === 0 ? (
        <NoTankYet user={user} />
      ) : (
        <>
          <label className="ts-field" style={{ marginTop: 12 }}>
            <span>Tank</span>
            <select value={pick} onChange={(e) => setPick(e.target.value)}>
              <option value="">Pick…</option>
              {myTanks.map((t) => (
                <option key={t.id} value={t.id} disabled={alreadyHere && t.id === alreadyHere.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
          {err && (
            <div role="alert" className="ts-err" style={{ marginTop: 8 }}>
              {err}
            </div>
          )}
          <div className="ts-row-body" style={{ marginTop: 12 }}>
            <button type="button" className="ts-save" onClick={submit} disabled={busy}>
              {busy ? '…' : alreadyHere ? 'Move' : 'Link'}
            </button>
            <a href="/app/" className="ts-chip">
              Cancel
            </a>
          </div>
          {alreadyHere && (
            <button
              type="button"
              className="ts-add-btn"
              style={{ marginTop: 20 }}
              onClick={() => linkTo(alreadyHere.id)}
              disabled={busy}
            >
              Keep it on {alreadyHere.name}: send to the display
            </button>
          )}
        </>
      )}
    </main>
  );
}

// A keeper with no tank of their own. A new account goes through tank setup and
// comes back here (App.jsx follows the remembered path once onboarding is
// done); until 2026-10-27 the page said "create one from the main app first"
// and the new keeper had to find the display's code again afterwards.
function NoTankYet({ user }) {
  const newKeeper = !user?.onboarding_completed_at;
  const setUp = () => {
    rememberAfterOnboarding(window.location.pathname + window.location.search);
    window.location.assign('/app/');
  };
  return (
    <>
      <p>
        A display shows one of your tanks, so set up your tank first. It takes about a minute.
        {newKeeper ? ' We bring you back here afterwards to finish linking.' : ''}
      </p>
      {newKeeper ? (
        <button
          type="button"
          className="ts-save"
          style={{ marginTop: 12, minHeight: 44 }}
          onClick={setUp}
        >
          Set up my tank
        </button>
      ) : (
        <>
          <p>Add a tank in TankSensei, then scan the display&rsquo;s code again.</p>
          <a href="/app/" className="ts-save" style={{ display: 'inline-block', marginTop: 12 }}>
            Open TankSensei
          </a>
        </>
      )}
    </>
  );
}

// Where a scan of a display's QR leads: link it, move it to another of the
// keeper's own tanks, or, when it is already on their only tank, say so.
// With one tank the move picker had nothing to pick (sweep 5, 2026-10-14).
export function claimMode(lookup, myTanks) {
  const here =
    lookup?.claimed_by_you && lookup.device?.tank_id
      ? myTanks.find((t) => t.id === lookup.device.tank_id) || null
      : null;
  if (!here) return { mode: 'link', here: null };
  return { mode: myTanks.some((t) => t.id !== here.id) ? 'move' : 'linked', here };
}

function ManualEntry({ value, onChange }) {
  const [draft, setDraft] = useState(value || '');
  return (
    <div style={{ marginTop: 14 }}>
      <label className="ts-field">
        <span>Device ID</span>
        <input
          type="text"
          autoCapitalize="off"
          autoComplete="off"
          spellCheck={false}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="printedreef-prod/View7-AB12"
        />
      </label>
      <button
        type="button"
        className="ts-save"
        style={{ marginTop: 8, minHeight: 44 }}
        onClick={() => onChange(draft.trim())}
        disabled={!draft.trim() || draft.trim() === value}
      >
        Use this ID
      </button>
    </div>
  );
}

function messageFor(code) {
  switch (code) {
    case 'device_not_seen_yet':
      return "We haven't heard from that device yet. Give it a minute and try again.";
    case 'device_already_claimed':
      return 'That device is already linked to another account. Contact support if it should be yours.';
    case 'tank_not_owned':
      return 'Only the tank owner can link a display to it.';
    case 'device_cleanup_pending':
      return 'This display is still being cleared from its last account. Try again in a minute.';
    default:
      return null;
  }
}

const shellStyle = {
  maxWidth: 520,
  margin: '0 auto',
  padding: '40px 20px',
};
